import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface GenieDocumentSuiteBannerProps {
  sourceCount: number;
  onPropose: () => void;
  isProposing: boolean;
}

/**
 * The entry point for the document suite — sits at the top of the Documents
 * page and offers to read every source and propose a full set of guides in one
 * click, instead of building one document at a time. Only worth showing once
 * the Genie has something to read.
 */
export const GenieDocumentSuiteBanner = ({
  sourceCount,
  onPropose,
  isProposing,
}: GenieDocumentSuiteBannerProps) => {
  const hasSources = sourceCount > 0;

  return (
    <div className="mb-8 overflow-hidden rounded-2xl border border-genie-primary/20 bg-gradient-to-r from-genie-primary/5 via-white to-genie-secondary/5 p-6 sm:p-7">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-genie-primary to-genie-secondary text-white shadow-lg shadow-genie-primary/30">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Build my document suite</h2>
            <p className="mt-0.5 max-w-xl text-sm text-gray-600">
              {hasSources
                ? `Let your Genie read all ${sourceCount} source${sourceCount === 1 ? "" : "s"} and propose a full set of guides — you review the list, then it drafts them all at once.`
                : "Add a source on the Sources tab first, then your Genie can read it and propose a full set of guides — you review the list, then it drafts them all at once."}
            </p>
          </div>
        </div>
        <Button
          onClick={onPropose}
          disabled={!hasSources || isProposing}
          className="bg-gradient-to-r from-genie-primary to-genie-secondary font-semibold text-white shadow-lg shadow-genie-primary/30 disabled:opacity-50"
        >
          {isProposing ? "Reading your sources…" : "Propose a suite"}
        </Button>
      </div>
    </div>
  );
};

export default GenieDocumentSuiteBanner;
